import type { AppState } from "../types/models";

const KEY = "GG:v1:state";

// Migration function to update old state format to new format
function migrateState(state: any): AppState {
    for (const cls of state.classes) {
        // Add tags array to all students if missing
        for (const student of cls.students) {
            if (!student.tags) {
                student.tags = [];
            }
        }

        // Initialize tagBehavior Map
        if (!cls.settings.tagBehavior) {
            cls.settings.tagBehavior = new Map();
        } else if (Array.isArray(cls.settings.tagBehavior)) {
            // Convert from serialized array format to Map
            cls.settings.tagBehavior = new Map(cls.settings.tagBehavior);
        }

        // Initialize tags array for existing classes
        if (!cls.settings.tags) {
            // Discover existing tags from students for backward compatibility
            const existingTags = new Set<string>();
            for (const student of cls.students) {
                student.tags.forEach((tag: string) => existingTags.add(tag));
            }
            cls.settings.tags = Array.from(existingTags).sort();
        }

        // Initialize history for existing classes
        if (!cls.history) {
            cls.history = { past: [], future: [] };
        }

        // Initialize seating chart mode fields
        if (!cls.settings.mode) {
            cls.settings.mode = 'groups';
        }
        if (!cls.seatingCharts) {
            cls.seatingCharts = [];
        }
        if (!cls.seatingHistory) {
            cls.seatingHistory = { past: [], future: [] };
        }

        // Migrate seatAssignments Maps in seating charts
        for (const chart of cls.seatingCharts) {
            if (chart.lastAssignment) {
                if (Array.isArray(chart.lastAssignment.seatAssignments)) {
                    chart.lastAssignment.seatAssignments = new Map(chart.lastAssignment.seatAssignments);
                } else if (!(chart.lastAssignment.seatAssignments instanceof Map)) {
                    chart.lastAssignment.seatAssignments = new Map();
                }
            }
        }

        // Migrate seating history entries
        if (cls.seatingHistory) {
            for (const entry of cls.seatingHistory.past) {
                if (Array.isArray(entry.assignment.seatAssignments)) {
                    entry.assignment.seatAssignments = new Map(entry.assignment.seatAssignments);
                }
            }
            for (const entry of cls.seatingHistory.future) {
                if (Array.isArray(entry.assignment.seatAssignments)) {
                    entry.assignment.seatAssignments = new Map(entry.assignment.seatAssignments);
                }
            }
        }

        // Remove deprecated fields
        if ((cls.settings as any).conflictPolicy) {
            delete (cls.settings as any).conflictPolicy;
        }

        // Remove priority from constraints
        for (const set of cls.constraints.mustTogether) {
            if ((set as any).priority) {
                delete (set as any).priority;
            }
        }
        for (const set of cls.constraints.mustApart) {
            if ((set as any).priority) {
                delete (set as any).priority;
            }
        }
    }

    return state as AppState;
}

export function load(): AppState | null {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        return migrateState(parsed);
    } catch (e) {
        console.error("❌ Failed to load state:", e);
        return null;
    }
}

export function save(state: AppState): void {
    try {
        // Clone state for serialization
        const serialized = structuredClone(state);

        // Convert tagBehavior Maps to arrays for JSON compatibility
        for (const cls of serialized.classes) {
            if (cls.settings.tagBehavior instanceof Map) {
                (cls.settings.tagBehavior as any) = Array.from(cls.settings.tagBehavior.entries());
            }

            // Serialize seatAssignments Maps in seating charts
            if (cls.seatingCharts) {
                for (const chart of cls.seatingCharts) {
                    if (chart.lastAssignment?.seatAssignments instanceof Map) {
                        (chart.lastAssignment.seatAssignments as any) = Array.from(
                            chart.lastAssignment.seatAssignments.entries()
                        );
                    }
                }
            }

            // Serialize seating history Maps
            if (cls.seatingHistory) {
                for (const entry of cls.seatingHistory.past) {
                    if (entry.assignment.seatAssignments instanceof Map) {
                        (entry.assignment.seatAssignments as any) = Array.from(
                            entry.assignment.seatAssignments.entries()
                        );
                    }
                }
                for (const entry of cls.seatingHistory.future) {
                    if (entry.assignment.seatAssignments instanceof Map) {
                        (entry.assignment.seatAssignments as any) = Array.from(
                            entry.assignment.seatAssignments.entries()
                        );
                    }
                }
            }
        }

        localStorage.setItem(KEY, JSON.stringify(serialized));
    } catch (e) {
        console.error("❌ Failed to save state:", e);
    }
}

export function reset(): void {
    try {
        localStorage.removeItem(KEY);
    } catch {}
}
